import { useCallback, useEffect, useRef, useState } from 'react'

interface AmbientSound {
  enabled: boolean
  started: boolean
  start: () => void
  toggle: () => void
  chime: (frequency?: number) => void
}

export function useAmbientSound(): AmbientSound {
  const context = useRef<AudioContext | null>(null)
  const master = useRef<GainNode | null>(null)
  const sources = useRef<OscillatorNode[]>([])
  const [enabled, setEnabled] = useState(true)
  const [started, setStarted] = useState(false)

  const createSoundscape = useCallback(() => {
    if (context.current) return

    const AudioContextClass = window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return

    const ctx = new AudioContextClass()
    const output = ctx.createGain()
    output.gain.setValueAtTime(0.0001, ctx.currentTime)
    output.gain.exponentialRampToValueAtTime(0.085, ctx.currentTime + 2.8)
    output.connect(ctx.destination)

    const lowpass = ctx.createBiquadFilter()
    lowpass.type = 'lowpass'
    lowpass.frequency.value = 680
    lowpass.Q.value = 1.4
    lowpass.connect(output)

    const partials = [55, 82.41, 110]
    partials.forEach((frequency, index) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      const panner = ctx.createStereoPanner()
      osc.type = index === 0 ? 'sine' : 'triangle'
      osc.frequency.value = frequency
      osc.detune.value = index * 3 - 2
      gain.gain.value = [0.42, 0.055, 0.025][index]
      panner.pan.value = index === 1 ? -0.42 : index === 2 ? 0.42 : 0
      osc.connect(gain).connect(panner).connect(lowpass)
      osc.start()
      sources.current.push(osc)
    })

    const lfo = ctx.createOscillator()
    const lfoGain = ctx.createGain()
    lfo.frequency.value = 0.075
    lfoGain.gain.value = 220
    lfo.connect(lfoGain).connect(lowpass.frequency)
    lfo.start()
    sources.current.push(lfo)

    context.current = ctx
    master.current = output
  }, [])

  const start = useCallback(() => {
    createSoundscape()
    context.current?.resume()
    setStarted(true)
  }, [createSoundscape])

  const toggle = useCallback(() => {
    if (!context.current) {
      start()
      setEnabled(true)
      return
    }
    const next = !enabled
    const now = context.current.currentTime
    master.current?.gain.cancelScheduledValues(now)
    master.current?.gain.setTargetAtTime(next ? 0.085 : 0.0001, now, 0.12)
    setEnabled(next)
  }, [enabled, start])

  const chime = useCallback((frequency = 392) => {
    const ctx = context.current
    const output = master.current
    if (!ctx || !output || !enabled) return

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const panner = ctx.createStereoPanner()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(frequency, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(frequency * 1.5, ctx.currentTime + 1.2)
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.055, ctx.currentTime + 0.04)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.4)
    panner.pan.value = Math.random() * 1.2 - 0.6
    osc.connect(gain).connect(panner).connect(output)
    osc.start()
    osc.stop(ctx.currentTime + 1.5)
  }, [enabled])

  useEffect(() => () => {
    sources.current.forEach((source) => source.stop())
    context.current?.close()
  }, [])

  return { enabled, started, start, toggle, chime }
}
